import { Component, OnInit, ChangeDetectionStrategy, ViewChild, ChangeDetectorRef } from '@angular/core';
import { CustomIcons } from '@shared/models/custom-icons';
import { MatDialogRef } from '@angular/material/dialog';
import { switchMap, take, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';
import { CatalogService } from '@shared/services/catalog.service';
import { DynamicFile } from '@shared/components/dynamic-catalog-files/dynamic-catalog-files/models';
import { DateTime } from 'luxon';
import { saveAs } from 'file-saver';
import { MatPaginationCustomComponent } from '@shared/components/mat-pagination-custom/mat-pagination-custom/mat-pagination-custom.component';
import { StartSearch } from '@shared/components/search-input/search-input.component';
import { Store } from '@ngxs/store';
import { RefreshCurrentCatalogStats } from '@shared/state/catalog/shared-catalog.action';
import { CatalogStateType } from '../../../../data/modules/catalog-items-data/models/catalog-item.service';
import { CatalogItemService } from '../../../../data/modules/catalog-items-data/services/catalog-item.service';

@Component({
  selector: 'app-dynamic-catalog-files',
  templateUrl: './dynamic-catalog-files.component.html',
  styleUrls: ['./dynamic-catalog-files.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DynamicCatalogFilesComponent implements OnInit {
  private _allFiles: DynamicFile[] = [];
  public icons = CustomIcons;
  public allCount: number | null;
  public displayedColumns = ['fileName'];
  public dataSource: MatTableDataSource<DynamicFile>;
  public catalogId: number;
  public set allFiles(files: DynamicFile[]) {
    this._allFiles = files;
  }
  public files$: Observable<
    {
      name: string;
      size: string;
    }[]
  >;
  @ViewChild(MatPaginationCustomComponent, { static: true }) public matPaginationCustom: MatPaginationCustomComponent;
  constructor(
    private dialogRef: MatDialogRef<DynamicCatalogFilesComponent>,
    private catalogService: CatalogService,
    private cdr: ChangeDetectorRef,
    private catalogItemService: CatalogItemService,
    private store: Store
  ) {}

  public ngOnInit(): void {
    const serverFormat = 'yyyyMMdd_HHmm';
    if (this.catalogId && this._allFiles.length === 0) {
      this.files$ = this.catalogService.getDynCatalogFiles(this.catalogId).pipe(
        tap(res => {
          this._allFiles = res
            .sort(
              (a, b) =>
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                DateTime.fromFormat(a.name.split('.')[0], serverFormat).toJSDate() -
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                DateTime.fromFormat(b.name.split('.')[0], serverFormat).toJSDate()
            )
            .reverse()
            .map(f => ({
              fileName: f.name,
              fileTitle: DateTime.fromFormat(f.name.split('.')[0], serverFormat).toFormat('dd.MM.yyyy_HH:mm'),
              loadingFile: false,
              fileSize: Math.ceil(+f.size / 1024 / 1024)
            }));

          this.allCount = this._allFiles.length;
          this.dataSource = new MatTableDataSource<DynamicFile>(this._allFiles);
          this.dataSource.paginator = this.matPaginationCustom.paginator;
        })
      );
    }
  }

  public close(): void {
    this.dialogRef.close();
  }

  public searchStart($event: StartSearch): void {
    const searchString = $event.searchString.trim();

    if (searchString === '') {
      this.dataSource = new MatTableDataSource<DynamicFile>(this._allFiles);
    } else {
      this.dataSource = new MatTableDataSource<DynamicFile>(this._allFiles.filter(x => x.fileTitle.includes(searchString)));
    }
    this.matPaginationCustom.setPageIndex(0);
    this.dataSource.paginator = this.matPaginationCustom.paginator;
  }

  public downloadFile(file: DynamicFile): void {
    file.loadingFile = true;
    this.cdr.detectChanges();
    this.catalogService
      .downloadDynCatalogFile(this.catalogId, file.fileName)
      .pipe(
        switchMap(res => {
          const saveFile = new Blob([res], { type: `application/gz` });
          saveAs(saveFile, file.fileName);
          file.loadingFile = false;
          this.cdr.detectChanges();

          return this.catalogItemService
            .saveCatalogStatistic({
              idOpenCatalog: this.catalogId,
              type: CatalogStateType.DownloadCatalog
            })
            .pipe(
              tap(() => {
                this.store.dispatch(new RefreshCurrentCatalogStats());
              })
            );
        }),
        take(1)
      )
      .subscribe();
  }

  public changePage(): void {
    this.dataSource.paginator = this.matPaginationCustom.paginator;
  }
}
